import { useRef, useEffect } from "react";
import axios from "axios";
import { Accordion, ToastContainer } from "react-bootstrap";
import "./OrderDetails.css";
import OrderDetailsModal from "../OrderDetailsModal/OrderDetailsModal";
import OrderTableDialog from "../components/OrderTableDialog";
import Tooltip from "@mui/material/Tooltip";
import { useOrderDetails } from "./useOrderDetails";
import { toast } from "react-toastify";
import down from "../../../Assets/Icons/down.png";

const OrderDetails = ({
  title,
  restaurant,
  restaurantIndex,
  selectedTable,
  setSelectedTable,
  selectedOkTable,
  setIsToggleOn,
  isToggleOn,
  DeliverySelfCollectionState,
  isServiceChargeEnabled: parentIsServiceChargeEnabled,
  handleServiceChargeToggle: parentHandleServiceChargeToggle,
  onSelfCollectionToggle,
  deliveryFee,
  onSubOrderTypeChange,
}) => {
  const {
    modalShow,
    setModalShow,
    tableModalShow,
    setTableModalShow,
    clickedRestaurant,
    cartData,
    selectOk,
    setselecteOk,
    totalSd,
    totalVat,
    totalVatOnSd,
    subTotalAmount,
    serviceCharge,
    serviceChargeVat,
    isServiceChargeEnabled,
    isDisabled,
    storedSwitchedItems,
    AntSwitch,
    handleShowTableModal,
    handleToggle,
    handleServiceChargeToggle,
    Total,
    TotalAfterCredit,
    isAccordionExpanded,
    setIsAccordionExpanded,
  } = useOrderDetails({
    title,
    restaurant,
    selectedTable,
    setSelectedTable,
    selectedOkTable,
    setIsToggleOn,
    isToggleOn,
    DeliverySelfCollectionState,
    isServiceChargeEnabled: parentIsServiceChargeEnabled,
    handleServiceChargeToggle: parentHandleServiceChargeToggle,
  });

  const latestRestaurantRef = useRef(restaurant);

  useEffect(() => {
    latestRestaurantRef.current = restaurant;
  }, [restaurant]);

  // API CALLS
  const updateSubOrderType = async (subOrderId) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_SECRET}/api/Cart/UpdateSubOrderType?subOrderId=${subOrderId}`,
      );
    } catch (error) {
      console.error("Failed to update subOrder type:", error);
      toast.error("Failed to save order type change");
      throw error;
    }
  };

  const UpdateIndividualItemOrderType = async (
    cartItemId,
    orderTypeId,
    orderId,
    subOrderId,
  ) => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_SECRET}/api/Cart/UpdateIndividualItemOrderType?cartItemId=${cartItemId}&orderTypeId=${orderTypeId}&orderId=${orderId}&oldSubOrderId=${subOrderId}`,
      );
    } catch (error) {
      console.error("Failed to update individual item order type:", error);
      toast.error("Failed to save item type change");
      throw error;
    }
  };

  // ACCORDION-LEVEL TOGGLE (entire subOrder)
  const handleSubOrderToggle = async (subOrder) => {
    const currentType = subOrder.orderTypeName;
    const newOrderType = currentType === "Delivery" ? "Collection" : "Delivery";
    const oldType = currentType;

    // Optimistic update: change subOrder type in global state
    onSubOrderTypeChange(subOrder.subOrderId, newOrderType);

    try {
      await updateSubOrderType(subOrder.subOrderId);
      if (onSelfCollectionToggle) {
        onSelfCollectionToggle(newOrderType === "Collection");
      }
      toast.success("Order type switched successfully");
    } catch (error) {
      toast.error("Failed to update order type. Reverting.");
      onSubOrderTypeChange(subOrder.subOrderId, oldType);
    }
  };

  // ITEM-LEVEL TOGGLE (single item)
  const handleItemToggle = async (item, subOrder, isCollection) => {
    const currentEffectiveType =
      item.effectiveOrderType ?? subOrder.orderTypeName;
    const newOrderType =
      currentEffectiveType === "Delivery" ? "Collection" : "Delivery";
    const prevEffectiveType = currentEffectiveType;

    onSubOrderTypeChange(subOrder.subOrderId, newOrderType, item.foodItemId);

    if (isCollection) {
      item.orderTypeId = "F54272CD-371F-44F9-A8AC-34C30C2DC4FE";
    } else {
      item.orderTypeId = "7CD55767-82AF-4912-AA5B-DCCBB9CCC68A";
    }

    try {
      await UpdateIndividualItemOrderType(
        item.cartItemId,
        item.orderTypeId,
        item.orderId,
        subOrder.subOrderId,
      );
      toast.success("Order type switched successfully");
    } catch (error) {
      if (prevEffectiveType === subOrder.orderTypeName) {
        onSubOrderTypeChange(subOrder.subOrderId, null, item.foodItemId);
      } else {
        onSubOrderTypeChange(
          subOrder.subOrderId,
          prevEffectiveType,
          item.foodItemId,
        );
      }
      toast.error("Failed to update item type. Reverting.");
    }
  };

  return (
    <Accordion defaultActiveKey={null} className="parent-body-shadow">
      <Accordion.Item eventKey="1">
        <Accordion.Header
          className="order-shadow"
          onClick={() => setIsAccordionExpanded(!isAccordionExpanded)}
        >
          <ToastContainer />
          <div className="d-flex align-items-center w-100">
            <div className="header-weight-bold">
              {title === "In-Store" ? "Dine-In" : title}
            </div>
            <div className="ms-2">
              <img
                src={down}
                alt="arrow"
                style={{
                  width: "16px",
                  height: "16px",
                  transform: isAccordionExpanded
                    ? "rotate(180deg)"
                    : "rotate(0deg)",
                  transition: "transform 0.3s ease",
                }}
              />
            </div>

            {/* Header Toggle — only for Delivery */}
            {title === "Delivery" && (
              <div className="d-flex align-items-center ms-2">
                <Tooltip title="Self-collection" arrow>
                  <span>
                    <AntSwitch
                      className="ms-2"
                      // Visually OFF in "Delivery" section
                      checked={false}
                      disabled={false}
                      onClick={(e) => {
                        e.stopPropagation();
                        // Find the first subOrder that has ANY item effectively set to "Delivery"
                        let targetSubOrder = null;
                        for (const subOrder of restaurant?.subOrders || []) {
                          const hasEffectiveDeliveryItem =
                            subOrder.itemDetails.some(
                              (item) =>
                                (item.effectiveOrderType ??
                                  subOrder.orderTypeName) === "Delivery",
                            );
                          if (hasEffectiveDeliveryItem) {
                            targetSubOrder = subOrder;
                            break;
                          }
                        }
                        if (targetSubOrder) {
                          handleSubOrderToggle(targetSubOrder);
                        }
                      }}
                      inputProps={{
                        "aria-label": "collection/delivery toggle",
                      }}
                    />
                  </span>
                </Tooltip>
                <span className="ms-2">Self-Collection</span>
              </div>
            )}

            {/* Table Selection for In-Store */}
            {title === "In-Store" && (
              <div
                className="ms-2 table-selection"
                onClick={() => {
                  if (restaurantIndex > 0 && !selectedTable) {
                    window.scrollTo({ top: 0, behavior: "smooth" });
                    toast.info(
                      "Please select previous restaurant's table first",
                    );
                    return;
                  }
                  handleShowTableModal(restaurant?.cartRestaurantId);
                }}
              >
                {selectOk?.tableName
                  ? selectOk.tableName
                  : selectedTable?.restaurantId === restaurant?.cartRestaurantId
                    ? selectedTable.tableName
                    : "Select Table"}
              </div>
            )}
          </div>
        </Accordion.Header>

        <Accordion.Body className="d-block px-2">
          <div className="order-details-wrapper">
            <div className="ms-auto d-flex">
              <div className="text-bold text-center margin-r">Base price</div>
              <div className="text-bold text-center">Total</div>
            </div>
          </div>

          {/* Food Items List */}
          {(() => {
            const itemsByType = {};
            restaurant.subOrders.forEach((subOrder) => {
              subOrder.itemDetails.forEach((item) => {
                const effectiveType =
                  item.effectiveOrderType ?? subOrder.orderTypeName;
                if (!itemsByType[effectiveType]) {
                  itemsByType[effectiveType] = [];
                }
                itemsByType[effectiveType].push({ item, subOrder });
              });
            });

            const itemsToRender = itemsByType[title] || [];

            return itemsToRender.map(({ item, subOrder }) => {
              const isCollection = title === "Collection";

              return (
                <div key={`${subOrder.subOrderId}-${item.foodItemId}`}>
                  <div className="order-details-wrapper mb-2">
                    <div>
                      <div className="food-name text-small">
                        <span>{item.foodItemName}</span>
                        {isCollection && (
                          <span className="self-collection-tag">
                            (Collection)
                          </span>
                        )}
                      </div>

                      {item.addOns.map((addon) => (
                        <div
                          key={addon.addOnId}
                          className="sub-options sub-options-color my-1 text-small"
                        >
                          {addon.addOnName}
                          {addon.options.map((option) => (
                            <div
                              key={option.optionId}
                              className="sub-options sub-options-color my-1 text-small"
                            >
                              {option.optionName}
                              {option.subOptions.map((subOption) => (
                                <div
                                  key={subOption.subOptionId}
                                  className="sub-options sub-options-margin text-small"
                                >
                                  <span>{subOption.subOptionName}</span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>

                    <div>
                      {!item.isCollectionOnly && (
                        <div className="item-toggle-wrapper">
                          <AntSwitch
                            checked={isCollection}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleItemToggle(item, subOrder, isCollection);
                            }}
                            inputProps={{
                              "aria-label": "collection/delivery toggle",
                            }}
                          />
                          <span className="item-toggle-label">Collection</span>
                        </div>
                      )}
                    </div>

                    <div className="d-flex">
                      <div className="base-price-container text-end">
                        <div className="text-black text-small">
                          {item.foodItemQuantity} * £{item.basePrice}
                        </div>
                        <div className="my-1 text-black text-small">
                          {item.addOns.map((addon) => (
                            <div className="sub-options sub-options-color ms-2 text-small">
                              <span>
                                {addon.addOnQuantity} * £{addon.addOnBasePrice}
                              </span>
                              {addon.options.map((option) => (
                                <div className="sub-options sub-options-color my-1 text-small">
                                  <span>
                                    {option.optionQuantity} * £
                                    {option.optionBasePrice}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="text-end">
                        <div className="text-black text-small">
                          £{(item.foodItemQuantity * item.basePrice).toFixed(2)}
                        </div>
                        <div className="my-1 text-black text-small">
                          {item.addOns.map((addon) => (
                            <div className="sub-options sub-options-color ms-2 text-small">
                              <span>
                                £
                                {(
                                  addon.addOnQuantity * addon.addOnBasePrice
                                ).toFixed(2)}
                              </span>
                              {addon.options.map((option) => (
                                <div className="sub-options sub-options-color my-1 text-small">
                                  <span>
                                    £
                                    {(
                                      option.optionQuantity *
                                      option.optionBasePrice
                                    ).toFixed(2)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            });
          })()}

          <hr />
          <div className="total-price">
            <div>
              <div className="my-1 text-small">Sub total</div>
              <div className="my-1 text-small">Total SD</div>
              {title === "In-Store" && (
                <div className="my-1 text-small d-flex align-items-center justify-content-between">
                  <span>Service charge ({restaurant.serviceCharge}%)</span>
                  {restaurant?.isServiceChargeCom === false && (
                    <Tooltip
                      title={
                        restaurant?.isServiceChargeCom === true
                          ? "Service charge is mandatory"
                          : "Toggle service charge"
                      }
                      arrow
                    >
                      <span>
                        <AntSwitch
                          checked={isServiceChargeEnabled}
                          disabled={restaurant?.isServiceChargeCom === true}
                          onChange={handleServiceChargeToggle}
                          inputProps={{ "aria-label": "service charge toggle" }}
                        />
                      </span>
                    </Tooltip>
                  )}
                </div>
              )}
              {title === "In-Store" &&
                isServiceChargeEnabled &&
                serviceCharge > 0 && (
                  <div className="my-1 text-small">
                    Service Charge VAT (20%)
                  </div>
                )}
              <div className="my-1 text-small">Total VAT</div>
              <div className="my-1 text-small">Total VAT On SD</div>
              <div className="my-1 text-small">Total</div>
              <div className="my-1 text-small">Total Credit</div>
              <div className="my-1 text-small">Delivery Fee</div>
            </div>
            <div className="align-right">
              <div className="my-1 text-small">
                £{subTotalAmount.toFixed(2)}
              </div>
              <div
                className="my-1 text-small text-primary text-decoration-underline text-cursor-pointer"
                onClick={() => setModalShow(true)}
              >
                £{totalSd.toFixed(2)}
              </div>
              <div className="my-1 text-small">£{serviceCharge.toFixed(2)}</div>
              {title === "In-Store" &&
                isServiceChargeEnabled &&
                serviceCharge > 0 && (
                  <div className="my-1 text-small">
                    £{serviceChargeVat.toFixed(2)}
                  </div>
                )}
              <div
                className="my-1 text-small text-primary text-decoration-underline text-cursor-pointer"
                onClick={() => setModalShow(true)}
              >
                £{totalVat.toFixed(2)}
              </div>
              <div className="my-1 text-small">£{totalVatOnSd.toFixed(2)}</div>
              <div className="my-1 text-small">£{Total().toFixed(2)}</div>
              <div className="my-1 text-small">£{deliveryFee.toFixed(2)}</div>
              <div className="my-1 text-small">
                {restaurant.restaurantCredit !== 0 && <span>-</span>}
                {Total().toFixed(2) >=
                  restaurant.minimumOrderPriceToGiveCredit &&
                restaurant.restaurantCredit !== 0
                  ? restaurant.creditLimitPerOrder
                  : "N/A"}
              </div>
            </div>
          </div>
          <hr />
          <div className="d-flex align-items-center justify-content-between">
            <div className="my-1">Total Payable</div>
            <div className="my-1 text-small">
              £{TotalAfterCredit().toFixed(2)}
            </div>
          </div>

          <OrderTableDialog
            show={tableModalShow}
            onHide={() => setTableModalShow(false)}
            restaurant={restaurant}
            restaurantId={clickedRestaurant}
            preSelectedTable={selectedTable}
            setParentSelectedTable={setSelectedTable}
            SetselecteOk={setselecteOk}
            selectOk={selectOk}
          />
          <OrderDetailsModal
            show={modalShow}
            onHide={() => setModalShow(false)}
            userCartData={
              cartData?.filter(
                (c) => c.cartRestaurantId === restaurant?.cartRestaurantId,
              ) || []
            }
            setParentSelectedTable={setSelectedTable}
            serviceChargeEnabledByRestaurant={{
              [restaurant?.cartRestaurantId]: isServiceChargeEnabled,
            }}
            handleServiceChargeToggle={() =>
              handleServiceChargeToggle(restaurant?.cartRestaurantId)
            }
          />
        </Accordion.Body>
      </Accordion.Item>
    </Accordion>
  );
};

export default OrderDetails;
